import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import {
  addBudgetVariableItem,
  deleteBudgetVariableItem,
  getBudgetCatalog,
  updateBudgetVariableItem,
} from '../../../../services/budgetAPI';
import { buildFormStyles, money, Pill, TreasuryDrawer } from '../treasury/treasuryUi';
import { flattenBudgetAccounts } from './BudgetAccountsPanel';
import { PartyFields, PartyHint } from './budgetPartyFields';

const RECURRENCE_OPTIONS = ['Mensual', 'Trimestral', 'Semestral', 'Anual', 'Puntual'];
const DRIVERS = [
  { value: 'percent_sales', label: '% sobre ventas' },
  { value: 'per_unit', label: 'Monto por unidad vendida' },
];
const DRIVER_TONE = { percent_sales: 'info', per_unit: 'success', amount: 'warning' };

const emptyForm = (year) => ({
  name: '',
  driver_kind: 'percent_sales',
  source_kind: 'sales',
  product_sku: '',
  percent: '',
  quantity: '',
  rate: '',
  amount: '',
  recurrence: 'Mensual',
  start_month: '1',
  end_month: '12',
  start_year: String(year || new Date().getFullYear()),
  end_year: String(year || new Date().getFullYear()),
  budget_account_id: '',
  notes: '',
  supplier: '',
  payment_term: 'contado',
});

const parseNumber = (value) => Number(String(value ?? '').replace(',', '.')) || 0;

const monthYearLabel = (month, year) => `${MONTH_LABELS[(Number(month) || 1) - 1]} ${year || ''}`.trim();

const yearOptionsFor = (budgetYear, extraYears = []) => {
  const years = new Set();
  const base = Number(budgetYear) || new Date().getFullYear();
  for (let year = base - 5; year <= base + 5; year += 1) years.add(year);
  extraYears.forEach((year) => {
    const value = Number(year);
    if (value) years.add(value);
  });
  return Array.from(years).sort((a, b) => a - b);
};

const driverLabel = (kind) => DRIVERS.find((row) => row.value === kind)?.label || kind;

const sourceLabel = (item) => {
  if (item.driver_kind === 'amount') return item.recurrence || 'Monto por período';
  if (item.source_kind === 'product') return item.product_sku ? `Producto · ${item.product_sku}` : 'Un producto';
  if (item.source_kind === 'manual') return 'Cantidad manual';
  return 'Todas las ventas';
};

const periodLabel = (item) => {
  if (item.driver_kind !== 'amount' && item.source_kind !== 'manual') return sourceLabel(item);
  const start = monthYearLabel(item.start_month, item.start_year);
  const end = monthYearLabel(item.end_month, item.end_year);
  if (item.recurrence === 'Puntual') return `${item.recurrence} · ${start}`;
  if (
    Number(item.start_month) === 1 &&
    Number(item.end_month) === 12 &&
    Number(item.start_year) === Number(item.end_year)
  ) {
    return item.start_year ? `${item.recurrence} · ${item.start_year}` : item.recurrence;
  }
  return `${item.recurrence} · ${start}–${end}`;
};

const BudgetVariableCostPanel = ({
  ui,
  isDarkMode = false,
  budget = null,
  budgetAccounts = [],
  canEdit = false,
  onChanged,
}) => {
  const styles = buildFormStyles(ui);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getBudgetCatalog();
        if (!cancelled) setProducts(Array.isArray(payload?.products) ? payload.products : []);
      } catch {
        if (!cancelled) setProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const costAccounts = useMemo(
    () => flattenBudgetAccounts(budgetAccounts).filter((row) => row.nature === 'costo' || row.nature === 'gasto'),
    [budgetAccounts]
  );
  const budgetYear = Number(budget?.year) || new Date().getFullYear();
  const yearOptions = yearOptionsFor(budgetYear, [form.start_year, form.end_year]);
  const needsProduct = form.source_kind === 'product';
  const needsPeriod = form.driver_kind === 'amount' || (form.driver_kind === 'per_unit' && form.source_kind === 'manual');

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (budget?.variable_items || []).filter((item) => {
      if (!q) return true;
      return `${item.name} ${item.product_sku} ${item.account_label} ${item.driver_kind} ${item.supplier}`.toLowerCase().includes(q);
    });
  }, [budget, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(budgetYear));
    setError('');
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      driver_kind: item.driver_kind || 'percent_sales',
      source_kind: item.source_kind || 'sales',
      product_sku: item.product_sku || '',
      percent: item.percent ? String(item.percent) : '',
      quantity: item.quantity ? String(item.quantity) : '',
      rate: item.rate ? String(item.rate) : '',
      amount: item.amount ? String(item.amount) : '',
      recurrence: item.recurrence || 'Mensual',
      start_month: String(item.start_month || 1),
      end_month: String(item.end_month || 12),
      start_year: String(item.start_year || budgetYear),
      end_year: String(item.end_year || item.start_year || budgetYear),
      budget_account_id: String(item.budget_account_id || ''),
      notes: item.notes || '',
      supplier: item.supplier || '',
      payment_term: item.payment_term || 'contado',
    });
    setError('');
    setShowForm(true);
  };

  const payloadFromForm = () => {
    const driver = form.driver_kind;
    const sourceKind = driver === 'amount' ? 'manual' : form.source_kind;
    return {
      name: form.name,
      driver_kind: driver,
      source_kind: sourceKind,
      product_sku: sourceKind === 'product' ? form.product_sku : '',
      percent: driver === 'percent_sales' ? parseNumber(form.percent) : 0,
      quantity: driver === 'per_unit' ? parseNumber(form.quantity) : 0,
      rate: driver === 'per_unit' ? parseNumber(form.rate) : 0,
      amount: driver === 'amount' ? parseNumber(form.amount) : 0,
      recurrence: form.recurrence,
      start_month: Number(form.start_month),
      end_month: Number(form.end_month),
      start_year: Number(form.start_year),
      end_year: Number(form.end_year),
      budget_account_id: form.budget_account_id ? Number(form.budget_account_id) : null,
      notes: form.notes,
      supplier: form.supplier,
      payment_term: form.payment_term || 'contado',
    };
  };

  const handleSave = async () => {
    if (!budget?.id) return;
    setBusy(true);
    setError('');
    try {
      const payload = payloadFromForm();
      const next = editing
        ? await updateBudgetVariableItem(budget.id, editing.id, payload)
        : await addBudgetVariableItem(budget.id, payload);
      setShowForm(false);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo guardar el costo variable.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar ${item.name}?`)) return;
    setBusy(true);
    setError('');
    try {
      const next = await deleteBudgetVariableItem(budget.id, item.id);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el costo variable.');
    } finally {
      setBusy(false);
    }
  };

  const setDriver = (driver) => {
    setForm((prev) => ({
      ...prev,
      driver_kind: driver,
      source_kind: driver === 'amount' ? 'manual' : driver === 'percent_sales' && prev.source_kind === 'manual' ? 'sales' : prev.source_kind,
    }));
  };

  return (
    <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>Costos variables</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
          Costos que varían según el nivel de ventas: un porcentaje de ingresos o un monto por unidad vendida.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar variable o cuenta…"
          style={{ ...styles.input, width: 260 }}
        />
        {canEdit ? (
          <button type="button" style={styles.primaryBtn} onClick={openCreate}>
            <Plus size={14} /> Agregar variable
          </button>
        ) : null}
      </div>
      {error && !showForm ? <p style={{ margin: '0 0 10px', color: ui.danger, fontSize: 12 }}>{error}</p> : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920 }}>
          <thead>
            <tr>
              {['Ítem', 'Tipo', 'Base', 'Valor', 'Anual', 'Cuenta', ''].map((label) => (
                <th
                  key={label || 'actions'}
                  style={{
                    textAlign: ['Valor', 'Anual'].includes(label) ? 'right' : 'left',
                    padding: 8,
                    borderBottom: `1px solid ${ui.border}`,
                    fontSize: 11,
                    color: ui.muted,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.id}>
                <td style={{ padding: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: ui.text }}>{item.name}</div>
                  <PartyHint item={item} ui={ui} />
                  {item.notes ? <div style={{ fontSize: 11, color: ui.muted }}>{item.notes}</div> : null}
                </td>
                <td style={{ padding: 8 }}>
                  <Pill label={driverLabel(item.driver_kind)} tone={DRIVER_TONE[item.driver_kind] || 'info'} isDarkMode={isDarkMode} />
                </td>
                <td style={{ padding: 8, fontSize: 12 }}>{periodLabel(item)}</td>
                <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>
                  {item.driver_kind === 'percent_sales'
                    ? `${Number(item.percent || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 })}%`
                    : item.driver_kind === 'per_unit'
                      ? money(item.rate)
                      : money(item.amount)}
                </td>
                <td style={{ padding: 8, textAlign: 'right', fontSize: 12 }}>{money(item.budget_total)}</td>
                <td style={{ padding: 8, fontSize: 12, color: ui.text, whiteSpace: 'nowrap' }}>
                  {item.account_label || item.account_code || '—'}
                </td>
                <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canEdit ? (
                    <>
                      <button type="button" style={styles.ghostBtn} onClick={() => openEdit(item)}>
                        <Pencil size={13} />
                      </button>
                      <button type="button" style={styles.ghostBtn} onClick={() => handleDelete(item)} disabled={busy}>
                        <Trash2 size={13} />
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                  Agrega comisiones, fletes u otros costos ligados a las unidades o al monto vendido.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showForm ? (
        <TreasuryDrawer
          ui={ui}
          title={editing ? 'Editar costo variable' : 'Nuevo costo variable'}
          subtitle="El monto mensual se calcula sobre las ventas o las unidades vendidas proyectadas."
          error={error}
          saving={busy}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        >
          <label style={styles.label}>
            Nombre
            <input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} style={styles.input} />
          </label>
          <label style={styles.label}>
            Tipo
            <select value={form.driver_kind} onChange={(event) => setDriver(event.target.value)} style={styles.input}>
              {DRIVERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {form.driver_kind === 'percent_sales' ? (
            <>
              <label style={styles.label}>
                Porcentaje
                <input value={form.percent} onChange={(event) => setForm((prev) => ({ ...prev, percent: event.target.value }))} style={styles.input} placeholder="Ej: 3" />
              </label>
              <label style={styles.label}>
                Base
                <select value={form.source_kind} onChange={(event) => setForm((prev) => ({ ...prev, source_kind: event.target.value }))} style={styles.input}>
                  <option value="sales">Todas las ventas proyectadas</option>
                  <option value="product">Un producto</option>
                </select>
              </label>
            </>
          ) : null}
          {form.driver_kind === 'per_unit' ? (
            <>
              <label style={styles.label}>
                Monto por unidad
                <input value={form.rate} onChange={(event) => setForm((prev) => ({ ...prev, rate: event.target.value }))} style={styles.input} />
              </label>
              <label style={styles.label}>
                Cantidad
                <select value={form.source_kind} onChange={(event) => setForm((prev) => ({ ...prev, source_kind: event.target.value }))} style={styles.input}>
                  <option value="sales">Unidades de todas las ventas</option>
                  <option value="product">Unidades de un producto</option>
                  <option value="manual">Cantidad manual</option>
                </select>
              </label>
              {form.source_kind === 'manual' ? (
                <label style={styles.label}>
                  Cantidad por período
                  <input value={form.quantity} onChange={(event) => setForm((prev) => ({ ...prev, quantity: event.target.value }))} style={styles.input} />
                </label>
              ) : null}
            </>
          ) : null}
          {form.driver_kind === 'amount' ? (
            <label style={styles.label}>
              Monto por período
              <input value={form.amount} onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))} style={styles.input} />
            </label>
          ) : null}
          {needsProduct ? (
            <label style={styles.label}>
              Producto
              <select value={form.product_sku} onChange={(event) => setForm((prev) => ({ ...prev, product_sku: event.target.value }))} style={styles.input}>
                <option value="">Selecciona un producto</option>
                {products.map((option) => (
                  <option key={option.product_sku} value={option.product_sku}>
                    {option.product_sku} · {option.product_name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {needsPeriod ? (
            <>
              <label style={styles.label}>
                Periodicidad
                <select value={form.recurrence} onChange={(event) => setForm((prev) => ({ ...prev, recurrence: event.target.value }))} style={styles.input}>
                  {RECURRENCE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={styles.label}>
                  Desde
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px', gap: 8 }}>
                    <select value={form.start_month} onChange={(event) => setForm((prev) => ({ ...prev, start_month: event.target.value }))} style={styles.input}>
                      {MONTH_LABELS.map((label, index) => (
                        <option key={`start-${label}`} value={String(index + 1)}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <select value={form.start_year} onChange={(event) => setForm((prev) => ({ ...prev, start_year: event.target.value }))} style={styles.input}>
                      {yearOptions.map((year) => (
                        <option key={`start-year-${year}`} value={String(year)}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
                {form.recurrence === 'Puntual' ? null : (
                  <label style={styles.label}>
                    Hasta
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px', gap: 8 }}>
                      <select value={form.end_month} onChange={(event) => setForm((prev) => ({ ...prev, end_month: event.target.value }))} style={styles.input}>
                        {MONTH_LABELS.map((label, index) => (
                          <option key={`end-${label}`} value={String(index + 1)}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <select value={form.end_year} onChange={(event) => setForm((prev) => ({ ...prev, end_year: event.target.value }))} style={styles.input}>
                        {yearOptions.map((year) => (
                          <option key={`end-year-${year}`} value={String(year)}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </label>
                )}
              </div>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
              El valor de cada mes se calcula con las ventas o unidades proyectadas en Estructura de ingresos.
            </p>
          )}
          <label style={styles.label}>
            Clasificación
            <select
              value={form.budget_account_id}
              onChange={(event) => setForm((prev) => ({ ...prev, budget_account_id: event.target.value }))}
              style={styles.input}
            >
              <option value="">Selecciona una cuenta</option>
              {costAccounts.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.optionLabel}
                </option>
              ))}
            </select>
          </label>
          <PartyFields form={form} setForm={setForm} styles={styles} />
          <label style={styles.label}>
            Notas
            <input value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} style={styles.input} />
          </label>
        </TreasuryDrawer>
      ) : null}
    </div>
  );
};

export default BudgetVariableCostPanel;
